/**
 * Attorney-related types for legal representation management.
 */

import { BaseEntity, Address, DateField } from './base';

// ============================================================================
// ATTORNEY
// ============================================================================

/**
 * Attorney entity - represents a legal professional who can be assigned to cases.
 * Attorneys are reusable across multiple cases and can represent different parties.
 */
export interface Attorney extends BaseEntity {
  /** Attorney's first name */
  firstName: string;
  /** Attorney's last name */
  lastName: string;
  /** Optional suffix (e.g., "Esq.", "Jr.", "III") */
  suffix?: string;
  /** Attorney's email address */
  email?: string;
  /** Office phone number */
  phoneNumber?: string;
  /** Mobile phone number */
  mobilePhone?: string;
  /** @deprecated Use mobilePhone instead */
  alternatePhone?: string;
  /** Attorney's website URL */
  website?: string;
  /** State bar number / license number */
  barNumber?: string;
  /** State(s) where attorney is licensed */
  barStates?: string[];
  /** Law firm name */
  firmName?: string;
  /** Firm address */
  firmAddress?: Address;
  /** Attorney's areas of practice */
  practiceAreas?: AttorneyPracticeArea[];
  /** Account this attorney belongs to */
  accountId: string;
  /** Current status */
  status: AttorneyStatus;
  /** Optional notes about this attorney (generic, applies across all cases) */
  notes?: string;
  /** Number of cases this attorney is assigned to (denormalized for display) */
  activeCaseCount?: number;

  // Legal Aid / Assistant information
  /** Name of the attorney's legal aid/assistant */
  legalAidName?: string;
  /** Email of the attorney's legal aid/assistant */
  legalAidEmail?: string;
  /** Phone of the attorney's legal aid/assistant */
  legalAidPhone?: string;
}

export type AttorneyStatus = 'active' | 'inactive';

export type AttorneyPracticeArea =
  | 'family_law'
  | 'child_custody'
  | 'divorce'
  | 'domestic_violence'
  | 'juvenile'
  | 'criminal'
  | 'guardian_ad_litem'
  | 'child_advocacy'
  | 'other';

export const ATTORNEY_PRACTICE_AREA_LABELS: Record<AttorneyPracticeArea, string> = {
  family_law: 'Family Law',
  child_custody: 'Child Custody',
  divorce: 'Divorce',
  domestic_violence: 'Domestic Violence',
  juvenile: 'Juvenile Law',
  criminal: 'Criminal Law',
  guardian_ad_litem: 'Guardian ad Litem',
  child_advocacy: 'Child Advocacy',
  other: 'Other',
};

// ============================================================================
// CASE-ATTORNEY RELATIONSHIP
// ============================================================================

/**
 * Represents the assignment of an attorney to a case.
 * An attorney can have multiple roles on a case (e.g., representing both a parent and a child).
 */
export interface CaseAttorney extends BaseEntity {
  /** The case this assignment belongs to */
  caseId: string;
  /** The attorney being assigned */
  attorneyId: string;
  /** Account for multi-tenancy */
  accountId: string;
  /** The role this attorney plays on the case */
  role: AttorneyRole;
  /** Custom label for 'other' role type */
  roleLabel?: string;
  /** If representing a parent, which parent */
  representedParentId?: string;
  /** If representing children (GAL, Minor's Counsel), which children */
  representedChildIds?: string[];
  /** When representation started */
  startDate?: DateField;
  /** When representation ended (null if ongoing) */
  endDate?: DateField;
  /** Whether this assignment is currently active */
  isActive: boolean;
  /** Notes about this specific assignment */
  notes?: string;
}

/**
 * The role an attorney plays on a case.
 */
export type AttorneyRole =
  | 'visiting_parent_attorney'  // Represents the visiting parent
  | 'custodial_parent_attorney' // Represents the custodial parent
  | 'childs_attorney'           // Child's Attorney / GAL (combined role)
  | 'petitioner_attorney'       // Represents the petitioner/plaintiff
  | 'respondent_attorney'       // Represents the respondent/defendant
  | 'gal'                       // Guardian ad Litem (represents child's interests)
  | 'minors_counsel'            // Minor's Counsel (represents child's wishes)
  | 'casa'                      // Court Appointed Special Advocate
  | 'mediator'                  // Court mediator
  | 'other';                    // Other legal role

export const ATTORNEY_ROLE_LABELS: Record<AttorneyRole, string> = {
  visiting_parent_attorney: "Visiting Parent Attorney",
  custodial_parent_attorney: "Custodial Parent Attorney",
  childs_attorney: "Child's Attorney/GAL",
  petitioner_attorney: "Petitioner's Attorney",
  respondent_attorney: "Respondent's Attorney",
  gal: 'Guardian ad Litem (GAL)',
  minors_counsel: "Minor's Counsel",
  casa: 'CASA Volunteer',
  mediator: 'Mediator',
  other: 'Other',
};

/** Primary attorney roles for dropdown selection (simplified list for common use cases) */
export const PRIMARY_ATTORNEY_ROLES: AttorneyRole[] = [
  'visiting_parent_attorney',
  'custodial_parent_attorney',
  'childs_attorney',
];

export const ATTORNEY_ROLE_DESCRIPTIONS: Record<AttorneyRole, string> = {
  visiting_parent_attorney: 'Legal representative for the visiting (non-custodial) parent',
  custodial_parent_attorney: 'Legal representative for the custodial parent',
  childs_attorney: "Attorney or Guardian ad Litem representing the child's interests",
  petitioner_attorney: 'Legal representative for the petitioner/plaintiff in the case',
  respondent_attorney: 'Legal representative for the respondent/defendant in the case',
  gal: 'Court-appointed advocate who represents the best interests of the child',
  minors_counsel: "Attorney who represents the child's expressed wishes",
  casa: 'Trained volunteer appointed by the court to advocate for children',
  mediator: 'Neutral third party who helps resolve disputes',
  other: 'Other legal role',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the full display name for an attorney
 */
export function getAttorneyDisplayName(attorney: Attorney): string {
  const name = `${attorney.firstName} ${attorney.lastName}`;
  return attorney.suffix ? `${name}, ${attorney.suffix}` : name;
}

/**
 * Get a formatted display string for an attorney with firm
 */
export function getAttorneyWithFirm(attorney: Attorney): string {
  const name = getAttorneyDisplayName(attorney);
  return attorney.firmName ? `${name} - ${attorney.firmName}` : name;
}
